import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { getNumberRequestCompletion } from "@/features/number-requests/completion";
import {
  useNumberRequest,
  useUpdateNumberRequest,
} from "@/features/number-requests/hooks/use-number-requests";
import {
  isEmailHandledKind,
  isGuidedNumberRequest,
  isNumberRequestValidated,
  type NumberRequestKind,
  type UpdateNumberRequestData,
} from "@/features/number-requests/types";
import { useTranslation } from "@/features/utils/i18n";
import { WarningCircleIcon } from "@phosphor-icons/react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { PageLayout } from "../../layout";
import { Deliveries } from "./components/deliveries";
import { DocumentChecklist } from "./components/document-checklist";
import { GuidedStatus } from "./components/guided-status";
import { HardwareChoice } from "./components/hardware-choice";
import { KindSwitcher } from "./components/kind-switcher";
import { NotificationEmail } from "./components/notification-email";
import { ProcessSteps } from "./components/process-steps";
import { RequestInformation } from "./components/request-information";
import { RequestNotes } from "./components/request-notes";
import { SipPanel } from "./components/sip-panel";

/**
 * Managing one number request, in two columns.
 *
 * Left is **what the client does**: their situation, then the information and
 * the documents we need, cut into blocks that collapse to a one-line summary
 * once answered (see `editable-block.tsx`). Right is **what happens to them**:
 * where the request stands, and the procedure it is walking through — reading
 * matter, nothing to fill in, so it can sit still while the left column is
 * worked through.
 *
 * The two columns are joined by the completion percentage: the blocks on the
 * left are counted and shown on the client's own step on the right, which is
 * the only place that answers "am I nearly done?".
 *
 * Everything re-renders against the current kind, so flipping the switcher
 * re-frames the whole page rather than opening a different one.
 */
export function NumberRequestPage() {
  const { t } = useTranslation("numberRequests");
  const { company: clientId, requestId } = useParams<{
    company: string;
    requestId: string;
  }>();

  const currentClient = useCurrentClient();
  const {
    data: request,
    isLoading,
    isError,
    refetch,
  } = useNumberRequest(clientId || "", requestId || "");
  const update = useUpdateNumberRequest(clientId || "");

  const backLink = `/${clientId}/settings/connectors`;

  // Returns whether it went through: a block only collapses into read mode on
  // a save the server accepted, otherwise the client would be left looking at a
  // summary of something we never stored.
  const save = async (data: UpdateNumberRequestData): Promise<boolean> => {
    if (!requestId) return false;
    try {
      await update.mutateAsync({ requestId, data });
      toast.success(t("numberRequests.messages.saved"));
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("numberRequests.messages.saveFailed"),
      );
      return false;
    }
  };

  const handleKindChange = async (kind: NumberRequestKind) => {
    if (!requestId) return;
    try {
      await update.mutateAsync({ requestId, data: { kind } });
      toast.success(t("numberRequests.messages.kindChanged"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("numberRequests.messages.saveFailed"),
      );
    }
  };

  const back = { label: t("numberRequests.detail.back"), path: backLink };

  if (isLoading) {
    return (
      <PageLayout
        width="medium"
        title={t("numberRequests.detail.title")}
        backTo={back}
      >
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </PageLayout>
    );
  }

  if (isError || !request) {
    return (
      <PageLayout
        width="medium"
        title={t("numberRequests.detail.title")}
        backTo={back}
      >
        {isError ? (
          <ErrorState onRetry={refetch} />
        ) : (
          <EmptyState
            icon={WarningCircleIcon}
            title={t("numberRequests.detail.notFound")}
          />
        )}
      </PageLayout>
    );
  }

  const documents = request.documents?.items || [];
  const client = currentClient?.client;

  /**
   * Validated: the number is live and the file is closed. The left column then
   * only *reports* — no kind switcher, no forms, and no checklist at all: the
   * papers were filed with the carrier, re-uploading one would change nothing,
   * and removing one would only lose it.
   */
  const validated = isNumberRequestValidated(request);

  /**
   * Opened through the guided creation: the dialog asked everything, so the
   * left column only shows where the request stands (see `GuidedStatus`).
   */
  const guided = isGuidedNumberRequest(request);

  return (
    <PageLayout
      width="medium"
      title={t(`numberRequests.kinds.${request.kind}`)}
      description={t(
        validated
          ? "numberRequests.detail.validatedDescription"
          : guided
            ? "numberRequests.detail.guidedDescription"
            : "numberRequests.detail.description",
      )}
      backTo={back}
    >
      <div className="grid items-start gap-6 lg:grid-cols-5">
        {/* What the client does. */}
        <div className="space-y-6 lg:col-span-3">
          {guided && (
            <GuidedStatus clientId={clientId || ""} request={request} />
          )}
          {guided && isEmailHandledKind(request.kind) && <SipPanel />}

          {!guided && (
            <>
              {!validated && (
                <KindSwitcher
                  kind={request.kind}
                  disabled={update.isPending}
                  onChange={handleKindChange}
                />
              )}

              <RequestInformation
                request={request}
                isSaving={update.isPending}
                onSave={save}
                readOnly={validated}
              />

              {!validated &&
                !isEmailHandledKind(request.kind) &&
                documents.length > 0 && (
                  <DocumentChecklist
                    clientId={clientId || ""}
                    requestId={request.id}
                    kind={request.kind}
                    documents={documents}
                    address={client?.address}
                    country={request.country || client?.default_country}
                  />
                )}

              {/* After the checklist rather than with the information: it is a
              small order, not a detail of the file, and it is only worth
              thinking about once the paperwork is out of the way. */}
              {request.kind === "port_existing" && (
                <HardwareChoice
                  request={request}
                  isSaving={update.isPending}
                  onSave={save}
                  readOnly={validated}
                />
              )}

              <RequestNotes
                request={request}
                isSaving={update.isPending}
                onSave={save}
                readOnly={validated}
              />

              {/* A SIP access is worked out by e-mail already, and ships nothing. */}
              {!isEmailHandledKind(request.kind) && (
                <NotificationEmail
                  request={request}
                  isSaving={update.isPending}
                  onSave={save}
                  readOnly={validated}
                />
              )}

              {/* A SIP request has no checklist to show — it gets the "we'll be in
              touch" panel in its place. */}
              {isEmailHandledKind(request.kind) && <SipPanel />}
            </>
          )}
        </div>

        {/* What happens to them. Sticky, because it is what they check against
            while filling the left column in. */}
        <div className="space-y-6 lg:sticky lg:top-6 lg:col-span-2">
          <ProcessSteps
            kind={request.kind}
            state={request.state}
            guided={guided}
            completion={
              guided ? undefined : getNumberRequestCompletion(request)
            }
          />
          <Deliveries request={request} />
        </div>
      </div>
    </PageLayout>
  );
}
